import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpenseDetailDrawer } from "@/components/expenses/ExpenseDetailDrawer";
import { ReceiptDialog } from "@/components/expenses/ReceiptDialog";
import { DataMovementDialog } from "@/components/expenses/DataMovementDialog";
import { StatusChip } from "@/components/StatusChip";
import { PersonAvatar } from "@/components/PersonAvatar";
import { Search, RotateCcw, FileImage, ChevronLeft, ChevronRight, ArrowUpRight, Timer, Pencil, Zap } from "lucide-react";
import { api, fmtUSD, fmtDate } from "@/lib/api";
import { toast } from "sonner";

const TAB_PARAM = { exported: "exported", not_exported: "archived" };

const FREQ_LABEL = { hourly: "hourly", nightly: "nightly", weekly: "weekly" };

const nextRun = (freq) => {
  const n = new Date();
  if (freq === "hourly") {
    const d = new Date(n); d.setMinutes(60, 0, 0); return d;
  }
  if (freq === "nightly") {
    const d = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate(), 2, 0, 0));
    if (d <= n) d.setUTCDate(d.getUTCDate() + 1);
    return d;
  }
  if (freq === "weekly") {
    const d = new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate(), 2, 0, 0));
    let add = (1 - d.getUTCDay() + 7) % 7;
    if (add === 0 && d <= n) add = 7;
    d.setUTCDate(d.getUTCDate() + add);
    return d;
  }
  return null;
};

const fmtCountdown = (ms) => {
  if (ms <= 0) return "0:00:00";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hms = `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return days > 0 ? `${days}d ${hms}` : hms;
};

const Countdown = ({ freq }) => {
  const [left, setLeft] = useState(() => nextRun(freq) - new Date());
  useEffect(() => {
    const t = setInterval(() => setLeft(nextRun(freq) - new Date()), 1000);
    return () => clearInterval(t);
  }, [freq]);
  return <span className="tabular-nums font-semibold text-white" data-testid="archive-countdown-timer">{fmtCountdown(left)}</span>;
};

export const ArchiveView = ({ tab, onCounts }) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, tab_counts: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [drawerId, setDrawerId] = useState(null);
  const [receiptId, setReceiptId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dm, setDm] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); setSelected(new Set()); }, [tab, debounced]);

  useEffect(() => {
    const onActed = () => setRefreshKey((k) => k + 1);
    window.addEventListener("aria:acted", onActed);
    return () => window.removeEventListener("aria:acted", onActed);
  }, []);

  useEffect(() => {
    if (dmOpen) return;
    api.get("/settings").then((r) => setDm(r.data.data_movement || null)).catch(() => {});
  }, [dmOpen, refreshKey]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ tab: TAB_PARAM[tab], sort: "date", dir: "desc", page: String(page), page_size: "25" });
    if (debounced) params.set("search", debounced);
    api.get(`/expenses?${params}`)
      .then((r) => { setData(r.data); onCounts?.(r.data.tab_counts || {}); })
      .catch(() => toast.error("Could not load the archive"))
      .finally(() => setLoading(false));
  }, [tab, debounced, page, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { setRefreshKey((k) => k + 1); setSelected(new Set()); };

  const restore = async (ids) => {
    try {
      await api.post("/expenses/bulk", { ids, action: "restore" });
      toast.success(ids.length > 1 ? `Restored ${ids.length} to the review queue` : "Restored to the review queue");
      refresh();
    } catch {
      toast.error("Restore failed");
    }
  };

  const exportNow = async (ids) => {
    try {
      await api.post("/expenses/bulk", { ids, action: "export" });
      toast.success(`Exported ${ids.length} expense${ids.length > 1 ? "s" : ""}${dm?.destinations?.length ? "" : " to NetSuite"}`);
      refresh();
    } catch {
      toast.error("Export failed");
    }
  };

  const exportAll = async () => {
    try {
      const r = await api.get(`/expenses?tab=archived&page_size=500`);
      const ids = r.data.items.map((e) => e.id);
      if (!ids.length) { toast.info("Nothing left to export"); return; }
      await exportNow(ids);
    } catch {
      toast.error("Export failed");
    }
  };

  const allChecked = data.items.length > 0 && data.items.every((e) => selected.has(e.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) data.items.forEach((e) => next.delete(e.id));
    else data.items.forEach((e) => next.add(e.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 25));
  const batchActive = !!(dm?.auto_export && dm?.destinations?.length && dm?.frequency && dm.frequency !== "realtime");
  const realtimeActive = !!(dm?.auto_export && dm?.destinations?.length && dm?.frequency === "realtime");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="archive-view">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11.5px] text-[#A3A3A3]">Everything approved and moved out of the queue — export the rest, or restore anything.</p>
        <span className="ml-auto text-[11.5px] tabular-nums text-[#6b6b6b]">{data.total} expenses</span>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search merchant, employee, GL…" data-testid="archive-search-input"
            className="h-8 w-52 border-[#222] bg-[#111] pl-8 text-[12.5px] text-white placeholder:text-[#555] sm:w-64" />
        </div>
      </div>

      {/* floating bulk toolbar */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div initial={{ opacity: 0, y: 14, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 14, x: "-50%" }}
            transition={{ duration: 0.18 }} data-testid="archive-bulk-toolbar"
            className="absolute bottom-14 left-1/2 z-30 flex max-w-[calc(100%-2rem)] flex-wrap items-center gap-2 rounded-full border border-[#2f2f2f] bg-[rgba(20,20,20,0.94)] px-4 py-2 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.9)] backdrop-blur-xl">
            <span className="text-[12.5px] font-medium">{selected.size} selected</span>
            <div className="ml-2 flex flex-wrap gap-1.5">
              {tab === "not_exported" && (
                <Button size="sm" onClick={() => exportNow([...selected])} data-testid="archive-bulk-export"
                  className="h-7 bg-[#4F8EF7] px-3 text-[12px] font-medium text-white hover:bg-[#699ef8]">
                  <ArrowUpRight size={12} className="mr-1" /> Export selected
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => restore([...selected])} data-testid="archive-bulk-restore"
                className="h-7 border-0 bg-[#1c1c1c] px-3 text-[12px] text-[#A3A3A3] hover:bg-[#242424] hover:text-white">
                <RotateCcw size={12} className="mr-1" /> Restore selected
              </Button>
            </div>
            <button onClick={() => setSelected(new Set())} className="ml-1 text-[11.5px] text-[#6b6b6b] hover:text-white" data-testid="archive-bulk-clear">
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border border-[#222222] bg-[#111111] card-shadow">
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[900px] text-left" data-testid="archive-table">
            <thead className="sticky top-0 z-10 bg-[#141414]">
              <tr>
                <th className="w-10 px-3 py-2.5">
                  <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="archive-select-all"
                    className="border-[#333] data-[state=checked]:bg-[#4F8EF7] data-[state=checked]:border-[#4F8EF7]" />
                </th>
                {["Merchant", "Employee", "Date", "Category", "Amount", "Status", "Receipt", ""].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* export-rules banner row */}
              {tab === "not_exported" && !loading && batchActive && data.items.length > 0 && (
                <tr className="bg-[rgba(79,142,247,0.08)]" data-testid="archive-export-countdown">
                  <td colSpan={9} className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Timer size={13} className="text-[#7FAEF9]" />
                      <span className="text-[12px] text-[#cfe0fb]">
                        Per the export rules, these will be auto-exported ({FREQ_LABEL[dm.frequency]}) in{" "}
                        <Countdown freq={dm.frequency} />
                      </span>
                      <div className="ml-auto flex items-center gap-1.5">
                        <button onClick={() => setDmOpen(true)} data-testid="archive-edit-rules"
                          className="flex h-6 items-center gap-1 rounded-md bg-[rgba(255,255,255,0.06)] px-2 text-[11px] text-[#c9c9c9] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white">
                          <Pencil size={10} /> Edit rules
                        </button>
                        <button onClick={exportAll} data-testid="archive-export-all"
                          className="flex h-6 items-center gap-1 rounded-md bg-[#4F8EF7] px-2 text-[11px] font-medium text-white transition-colors hover:bg-[#699ef8]">
                          <Zap size={10} /> Export all now
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {tab === "not_exported" && !loading && realtimeActive && (
                <tr className="bg-[rgba(34,197,94,0.07)]" data-testid="archive-realtime-note">
                  <td colSpan={9} className="px-4 py-2">
                    <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-[#9fdcb6]">
                      <Zap size={12} className="text-[#4ADE80]" />
                      Real-time export is on — newly approved expenses skip this queue.
                      <button onClick={() => setDmOpen(true)} data-testid="archive-edit-rules"
                        className="ml-auto flex h-6 items-center gap-1 rounded-md bg-[rgba(255,255,255,0.06)] px-2 text-[11px] text-[#c9c9c9] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white">
                        <Pencil size={10} /> Edit rules
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {tab === "not_exported" && !loading && !dm?.auto_export && data.items.length > 0 && (
                <tr className="bg-[#0d0d0d]" data-testid="archive-no-rules-note">
                  <td colSpan={9} className="px-4 py-2">
                    <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-[#6b6b6b]">
                      No auto-export policy is active — these stay here until you export them.
                      <button onClick={() => setDmOpen(true)} data-testid="archive-edit-rules"
                        className="ml-auto flex h-6 items-center gap-1 rounded-md bg-[rgba(255,255,255,0.06)] px-2 text-[11px] text-[#c9c9c9] transition-colors hover:bg-[rgba(255,255,255,0.12)] hover:text-white">
                        <Pencil size={10} /> Set up rules
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-[#181818]">
                    <td colSpan={9} className="px-4 py-3"><Skeleton className="h-5 w-full bg-[#1a1a1a]" /></td>
                  </tr>
                ))
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-14 text-center text-[13px] text-[#A3A3A3]">
                    {tab === "exported" ? "Nothing exported yet — approved expenses land here once they flow downstream." : "Nothing waiting — everything has been exported."}
                  </td>
                </tr>
              ) : (
                data.items.map((e, i) => (
                  <tr key={e.id} onClick={() => setDrawerId(e.id)} data-testid={`archive-row-${e.id}`}
                    style={{ animationDelay: `${Math.min(i, 14) * 22}ms` }}
                    className="anim-fade-up cursor-pointer border-b border-[#181818] transition-colors hover:bg-[#161616]">
                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                      <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleOne(e.id)}
                        data-testid={`archive-row-checkbox-${e.id}`}
                        className="border-[#333] data-[state=checked]:bg-[#4F8EF7] data-[state=checked]:border-[#4F8EF7]" />
                    </td>
                    <td className="px-3 py-2.5 text-[13px] font-medium text-white">{e.merchant}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <PersonAvatar name={e.employee_name} size={20} />
                        <span className="text-[12.5px] text-[#c9c9c9]">{e.employee_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[12px] tabular-nums text-[#A3A3A3]">{fmtDate(e.date)}</td>
                    <td className="px-3 py-2.5 text-[12px] text-[#A3A3A3]">{e.category}</td>
                    <td className="px-3 py-2.5 text-[13px] font-medium tabular-nums text-white">{fmtUSD(e.amount)}</td>
                    <td className="px-3 py-2.5"><StatusChip status={e.status} /></td>
                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                      {e.has_receipt ? (
                        <button onClick={() => setReceiptId(e.id)} data-testid={`archive-receipt-${e.id}`} title="View receipt"
                          className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(79,142,247,0.12)] text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)] hover:text-white">
                          <FileImage size={13} />
                        </button>
                      ) : (
                        <span className="text-[12px] text-[#3a3a3a]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {tab === "not_exported" && (
                          <button onClick={() => exportNow([e.id])} data-testid={`archive-export-${e.id}`}
                            className="flex items-center gap-1.5 rounded-lg bg-[rgba(79,142,247,0.12)] px-2.5 py-1 text-[11.5px] text-[#7FAEF9] transition-colors hover:bg-[rgba(79,142,247,0.24)] hover:text-white">
                            <ArrowUpRight size={11} /> Export
                          </button>
                        )}
                        <button onClick={() => restore([e.id])} data-testid={`archive-restore-${e.id}`}
                          className="flex items-center gap-1.5 rounded-lg bg-[#1c1c1c] px-2.5 py-1 text-[11.5px] text-[#A3A3A3] transition-colors hover:bg-[#242424] hover:text-white">
                          <RotateCcw size={11} /> Restore
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-[#1e1e1e] px-4 py-2.5">
          <span className="text-[11.5px] text-[#6b6b6b]">
            Page {page} of {totalPages} · <span className="tabular-nums">{data.total}</span> results
          </span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="archive-prev-page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#222] text-[#A3A3A3] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30">
              <ChevronLeft size={13} />
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="archive-next-page"
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#222] text-[#A3A3A3] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30">
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>

      <ExpenseDetailDrawer expenseId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)}
        onChanged={refresh} />
      <ReceiptDialog expenseId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} onOpenDetails={setDrawerId} />
      <DataMovementDialog open={dmOpen} onOpenChange={setDmOpen} />
    </div>
  );
};
